import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useUsers } from "@/hooks/useUsers";
import { useAllCompanies } from "@/hooks/useCompanies";
import { useRoles } from "@/hooks/useRoles";
import { UserResponse, UserRequest } from "@/types/api";
import { formatRole } from "@/lib/utils";

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserResponse | null;
  onSave?: (data: UserRequest) => void;
}

const EditUserModal = ({ open, onOpenChange, user, onSave }: EditUserModalProps) => {
  const [formData, setFormData] = useState<UserRequest>({
    name: "",
    email: "",
    password: "", // Campo obrigatório no backend
    companyId: "",
    rolesIdList: [],
  });

  const { updateUser, isUpdating } = useUsers();
  const { companies } = useAllCompanies();
  const { roles } = useRoles();

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.displayName, // Usar displayName do backend
        email: user.email,
        password: "", // Deixar vazio - usuário pode optar por não alterar
        companyId: user.company?.id || "",
        rolesIdList: [], // Roles vêm como string[] no backend, não como objetos
      });
    }
  }, [user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    // Se a senha estiver vazia, usar uma senha placeholder
    // O backend deve ser modificado para não alterar a senha se ela estiver vazia
    const dataToSend = {
      ...formData,
      password: formData.password || "unchanged_password_placeholder"
    };

    if (onSave) {
      // Usa a função onSave passada via props (para casos customizados)
      onSave(dataToSend);
    } else {
      // Usa a integração padrão com o backend
      updateUser(
        { id: user.id, userData: dataToSend },
        {
          onSuccess: () => {
            onOpenChange(false);
          },
        }
      );
    }
  };

  const handleInputChange = (field: keyof UserRequest, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleChange = (roleId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      rolesIdList: checked
        ? [...prev.rolesIdList, roleId]
        : prev.rolesIdList.filter(id => id !== roleId)
    }));
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
          <DialogDescription>
            Atualize os dados do usuário {user.displayName}.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Nome completo"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="email@exemplo.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Nova Senha (opcional)</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              placeholder="Deixe vazio para manter a senha atual"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Select
              value={formData.companyId}
              onValueChange={(value) => handleInputChange("companyId", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies?.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={role.id}
                    checked={formData.rolesIdList.includes(role.id)}
                    onCheckedChange={(checked) => handleRoleChange(role.id, checked as boolean)}
                  />
                  <Label htmlFor={role.id} className="text-sm">
                    {formatRole(role.name)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isUpdating}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
